import { OpenId } from '@staccx/open-id'
import MyCar from './pages/cars/components/MyCar'
import Redir from './pages/redir/routes/Redir'
import { BrowserRouter, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import Home from './pages/home/routes/Home'
import Invoices from './pages/invoices/routes/Invoices'
import Help from './pages/help/routes/Help'
import Information from './pages/information/routes/Information'
import Authorization from './pages/login/components/Authorization'
import featureToggles from './autoplan_config'
import ChooseCarVariant from './pages/carConfiguration/routes/ChooseCarVariant'
import LandingPage from './pages/carConfiguration/routes/LandingPage'
import ConfigureBaseCar from './pages/carConfiguration/routes/ConfigureBaseCar'
import ConfigureCarOptions from './pages/carConfiguration/routes/ConfigureCarOptions'
import ConfigureWheelPackages from './pages/carConfiguration/routes/ConfigureWheelPackages'
import ConfigureAgreements from './pages/carConfiguration/routes/ConfigureAgreements'
import Summary from './pages/carConfiguration/routes/Summary'
import Confirmation from './pages/carConfiguration/routes/Confirmation'
import NoAccess from './pages/carConfiguration/routes/NoAccess'
import UnknownError from './pages/carConfiguration/routes/UnknownError'
import { useUser, useUserContext } from './context/UserProvider'
import {
  CHOOSE_CAR,
  CONFIGURE_AGREEMENTS,
  CONFIGURE_BASE_CAR,
  CONFIGURE_CAR_OPTIONS,
  CONFIGURE_WHEEL_PACKAGES,
  CONFIRMATION,
  LANDING_PAGE,
  NO_ACCESS,
  SUMMARY,
  UNKNOWN_ERROR,
} from './pages/carConfiguration/hooks/useCarConfiguration'
import FleetReport from './pages/reports/v3/routes/FleetReport'
import LeasingReport from './pages/reports/v3/routes/LeasingReport'
import FleetReportDetails from './pages/reports/v3/routes/FleetReportDetails'
import FleetReportExpense from './pages/reports/v3/routes/FleetReportExpense'
import InvoiceReport from './pages/reports/v3/routes/InvoiceReport'
import InvoiceReportHeaders from './pages/reports/v3/routes/InvoiceReportHeaders'
import InvoiceReportDetails from './pages/reports/v3/routes/InvoiceReportDetails'
import EnvironmentReport from './pages/reports/v3/routes/EnvironmentReport'
import FuelReport from './pages/reports/v3/routes/FuelReport'
import FuelReportDetails from './pages/reports/v3/routes/FuelReportDetails'
import FuelReportExpenseDetails from './pages/reports/v3/routes/FuelReportExpenseDetails'
import PurchaseReport from './pages/reports/v3/routes/PurchaseReport'
import PurchaseReportDetails from './pages/reports/v3/routes/PurchaseReportDetails'
import PurchaseReportAccountDetails from './pages/reports/v3/routes/PurchaseReportAccountDetails'
import TollReport from './pages/reports/v3/routes/TollReport'
import TollReportDetails from './pages/reports/v3/routes/TollReportDetails'
import TollReportAccountDetails from './pages/reports/v3/routes/TollReportAccountDetails'
import FleetExport from './pages/reports/routes/FleetExport'
import ReportsOverview from './pages/reports/routes/ReportOverview'
import ReportsLayout from './pages/reports/v3/components/ReportsLayout'
import PageLayout from './components/PageLayout'
import AppLoader from './components/loaders/AppLoader'
import UserProfile from './pages/profile/routes/UserProfile'
import MileageReport from './pages/reports/routes/MileageReport'
import MileageReportDetails from './pages/reports/routes/MileageReportDetails'
import Tasks from './pages/tasks/routes/Tasks'
import BudgetSelection from './pages/tasks/routes/BudgetSelection'
import SigningComplete from './pages/tasks/routes/SigningComplete'
import SigningCallback from './pages/tasks/routes/SigningCallback'
import BudgetSelectionComplete from './pages/tasks/routes/BudgetSelectionComplete'
import SigningBatchComplete from './pages/tasks/routes/SigningBatchComplete'
import SigningBatchLoader from './pages/tasks/routes/SigningBatchLoader'
import { ArchiveWrapper } from './pages/archive/routes/ArchiveWrapper'
import { CarDetailsWrapper } from './pages/cars/routes/CarDetailsWrapper'
import CarParkWrapper from './pages/cars/components/V3/CarPark/CarParkWrapper'
import { Drivers } from './pages/contacts/V3/drivers/Drivers'

const { customerCarConfiguration } = featureToggles

const AuthenticatedLayout = () => {
  const userData = useUser()
  const { pathname } = useLocation()

  return (
    <OpenId>
      {({ user }) => {
        if (!user) {
          return <Authorization />
        }
        if (!userData && pathname !== '/logout') {
          return <AppLoader />
        }
        return (
          <PageLayout>
            <Outlet />
          </PageLayout>
        )
      }}
    </OpenId>
  )
}

const AutoplanRoutes = () => {
  const { hasConfigurationAccess } = useUserContext()

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path={'*'}
          element={<AuthenticatedLayout />}
        >
          <Route path={'*'} element={<Redir />} />
          <Route path={'redir'} element={<Redir />} />
          <Route path={'archive'} element={<ArchiveWrapper />} />
          <Route path={'home'} element={<Home />} />
          <Route path={'cars'} element={<CarParkWrapper />} />
          <Route path={'cars/:id'} element={<CarDetailsWrapper />} />
          <Route path={'drivers'} element={<Drivers />} />
          <Route path={'my-car'} element={<MyCar />} />
          <Route path={'reports'}>
            <Route index element={<ReportsOverview />} />
            <Route path={'leasing'} element={<ReportsLayout />}>
              <Route index element={<LeasingReport />} />
            </Route>
            <Route path={'fleet-export'} element={<FleetExport />} />

            <Route path={'environment'} element={<ReportsLayout />}>
              <Route index element={<EnvironmentReport />} />
            </Route>

            <Route path="purchase" element={<ReportsLayout />}>
              <Route index element={<PurchaseReport />} />
              <Route path={':date'} element={<PurchaseReportDetails />} />
              <Route path={':date/:account'} element={<PurchaseReportAccountDetails />} />
            </Route>

            <Route path={'toll'} element={<ReportsLayout />}>
              <Route index element={<TollReport />} />
              <Route path={':date'} element={<TollReportDetails />} />
              <Route path={':date/:account'} element={<TollReportAccountDetails />} />
            </Route>

            <Route path={'fleet'} element={<ReportsLayout />}>
              <Route index element={<FleetReport />} />
              <Route path={'account/:account'} element={<FleetReportDetails />} />
              <Route path={'expense/:account/:itemNo'} element={<FleetReportExpense />} />
            </Route>

            <Route path={'invoice'} element={<ReportsLayout />}>
              <Route index element={<InvoiceReport />} />
              <Route path={':consolidatedInvoiceNo'} element={<InvoiceReportHeaders />} />
              <Route path={':consolidatedInvoiceNo/:invoiceNo'} element={<InvoiceReportDetails />} />
            </Route>

            <Route path={'fuel'} element={<ReportsLayout />}>
              <Route index element={<FuelReport />} />
              <Route path={':date'} element={<FuelReportDetails />} />
              <Route path={':date/:account'} element={<FuelReportExpenseDetails />} />
            </Route>

            <Route path={'mileage'}>
              <Route index element={<MileageReport />} />
              <Route path={'account/:account'} element={<MileageReportDetails />} />
            </Route>
          </Route>

          <Route path={'invoices'} element={<ReportsLayout />}>
            <Route index element={<Invoices />} />
          </Route>
          <Route path={'document-archive'} element={<Information />} />
          <Route path={'help'} element={<Help />} />
          <Route path={'tasks'} element={<Tasks />} />
          <Route path={'tasks/budget-selection/:id'} element={<BudgetSelection />} />
          <Route path={'tasks/budget-selection/complete'} element={<BudgetSelectionComplete />} />
          <Route path={'tasks/signing/callback'} element={<SigningCallback />} />
          <Route path={'tasks/signing-batch/:flowId'} element={<SigningBatchLoader />} />
          <Route path={'tasks/signing/complete/:signingType?'} element={<SigningComplete />} />
          <Route path={'tasks/signing-batch/complete'} element={<SigningBatchComplete />} />
          <Route path={'profile'} element={<UserProfile />} />
          {customerCarConfiguration && hasConfigurationAccess && (
            <>
              <Route key={LANDING_PAGE} path={'car-order'} element={<LandingPage />} />
              <Route key={CHOOSE_CAR} path={`car-order/:flowId/${CHOOSE_CAR}`} element={<ChooseCarVariant />} />
              <Route
                key={CONFIGURE_BASE_CAR}
                path={`car-order/:flowId/${CONFIGURE_BASE_CAR}`}
                element={<ConfigureBaseCar />}
              />
              <Route
                key={CONFIGURE_CAR_OPTIONS}
                path={`car-order/:flowId/${CONFIGURE_CAR_OPTIONS}`}
                element={<ConfigureCarOptions />}
              />
              <Route
                key={CONFIGURE_WHEEL_PACKAGES}
                path={`car-order/:flowId/${CONFIGURE_WHEEL_PACKAGES}`}
                element={<ConfigureWheelPackages />}
              />
              <Route
                key={CONFIGURE_AGREEMENTS}
                path={`car-order/:flowId/${CONFIGURE_AGREEMENTS}`}
                element={<ConfigureAgreements />}
              />
              <Route key={SUMMARY} path={`car-order/:flowId/${SUMMARY}`} element={<Summary />} />
              ,
              <Route key={CONFIRMATION} path={`car-order/:flowId/${CONFIRMATION}`} element={<Confirmation />} />
              <Route path={'car-order/:flowId/error/'}>
                <Route key={NO_ACCESS} path={NO_ACCESS} element={<NoAccess />} />
                <Route key={UNKNOWN_ERROR} path={UNKNOWN_ERROR} element={<UnknownError />} />
              </Route>
            </>
          )}
          <Route path={'logout'} element={<OpenId>{({ logout }) => logout()}</OpenId>} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export default AutoplanRoutes
